import React from "react";
import { useNavigate } from "react-router-dom";
import { WebPaths } from "../../../../../routes/data";
import { HiAcademicCap } from "react-icons/hi2";
import { IoPeopleCircleOutline } from "react-icons/io5";
import {
  FaPlaneDeparture,
  FaArrowRight,
  FaBook,
  FaGraduationCap,
  FaSchool,
  FaFileInvoiceDollar,
  FaReceipt,
  FaSuitcaseRolling,
} from "react-icons/fa6";
import { motion } from "framer-motion";
import Button from "../../../components/ui/Button";

const features = [
  {
    name: "Student Visas (F1 & M1)",
    description:
      "For those seeking to study in the United States, whether in academic or vocational programs.",
    href: `${WebPaths.checkEligibility}/ds160`,
    icon: HiAcademicCap,
    theme: {
      bg: "bg-blue-50",
      text: "text-blue-600",
      border: "border-blue-100",
      hover: "hover:border-blue-300",
      pill: "bg-blue-100 text-blue-700",
    },
    tag: "Most Popular",
    visual: (
      <div className="relative mt-4 min-h-[160px] overflow-hidden rounded-xl bg-blue-50/30 p-4 border border-blue-100/50 group-hover/card:border-blue-200 transition-colors duration-500">
        <div className="flex flex-col gap-2">
          <div className="h-2 w-24 rounded bg-blue-200/50" />
          <div className="flex items-center gap-2">
            <div className="h-7 w-32 rounded-lg bg-blue-600 shadow-sm transition-transform duration-500 group-hover/card:scale-[1.03]" />
            <div className="h-7 w-12 rounded-lg bg-white border border-blue-100" />
          </div>
          <div className="h-2 w-16 rounded bg-blue-100/50" />
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2">
          {[FaBook, FaGraduationCap, FaSchool, FaBook].map((Icon, i) => (
            <motion.div
              key={i}
              animate={{ y: [0, -4, 0] }}
              transition={{
                duration: 3,
                delay: i * 0.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className={`h-11 rounded-lg border border-blue-100 bg-white flex items-center justify-center text-blue-400 transition-all duration-500 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 ${
                i === 2 ? "shadow-sm ring-1 ring-blue-100" : ""
              }`}
            >
              <Icon className="text-lg" />
            </motion.div>
          ))}
        </div>
      </div>
    ),
  },
  {
    name: "Dependent Visas (F2)",
    description:
      "For dependents (spouse and children) of F1 visa holders, allowing them to join their family in the U.S.",
    href: `${WebPaths.checkEligibility}/ds160`,
    icon: IoPeopleCircleOutline,
    theme: {
      bg: "bg-amber-50",
      text: "text-amber-600",
      border: "border-amber-100",
      hover: "hover:border-amber-300",
      pill: "bg-amber-100 text-amber-700",
    },
    tag: "Family",
    visual: (
      <div className="relative mt-4 min-h-[160px] overflow-hidden rounded-xl bg-amber-50/30 p-4 border border-amber-100/50 group-hover/card:border-amber-200 transition-colors duration-500">
        <div className="space-y-3">
          {[
            { label: "Spouse", icon: "👤", delay: 0 },
            { label: "Child", icon: "👥", delay: 0.5 },
          ].map((item, i) => (
            <motion.div
              key={i}
              animate={{ x: [0, 5, 0] }}
              transition={{
                duration: 4,
                delay: item.delay,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className={`flex items-center gap-3 p-3 rounded-xl bg-white border border-amber-100 shadow-sm transition-shadow duration-500 group-hover/card:shadow-md ${
                i === 1 ? "ml-6" : ""
              }`}
            >
              <div className="h-9 w-9 rounded-full bg-amber-50 flex items-center justify-center border border-amber-100 text-xs shadow-inner">
                {item.icon}
              </div>
              <div className="flex-1 space-y-1.5">
                <div className="h-2 w-20 rounded bg-amber-100/50" />
                <div className="flex gap-1">
                  <div className="h-1.5 w-8 rounded bg-amber-100" />
                  <div className="h-1.5 w-4 rounded bg-amber-50" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    ),
  },
  {
    name: "Travel Visas (B1/B2)",
    description:
      "For short-term visitors traveling for business (B1) or leisure (B2) purposes.",
    href: `${WebPaths.checkEligibility}/ds160`,
    icon: FaPlaneDeparture,
    theme: {
      bg: "bg-green-50",
      text: "text-green-600",
      border: "border-green-100",
      hover: "hover:border-green-300",
      pill: "bg-green-100 text-green-700",
    },
    tag: "Tourism",
    visual: (
      <div className="relative mt-4 min-h-[160px] overflow-hidden rounded-xl bg-green-50/30 p-4 border border-green-100/50 group-hover/card:border-green-200 transition-colors duration-500">
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: FaFileInvoiceDollar, label: "Invoice" },
            { icon: FaReceipt, label: "Receipt" },
            { icon: FaSuitcaseRolling, label: "Luggage" },
            { icon: FaPlaneDeparture, label: "Flight" },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white rounded-lg p-2 border border-green-100 flex flex-col items-center justify-center gap-1 shadow-sm"
            >
              <item.icon className="text-green-400 text-lg" />
              <div className="h-1 w-8 bg-green-50 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
];

const Offers = () => {
  const navigate = useNavigate();

  return (
    <section id="popular-packages" className="py-24 sm:py-32 bg-white-f9">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center mb-16">
          <h2 className="text-3xl font-light font-spartan tracking-tight text-black-1a sm:text-4xl text-balance">
            Popular{" "}
            <span className="text-blue-00 italic mx-1">Packages</span>
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Choose the right visa path for your journey. We guide you every step
            of the way.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`group/card relative bg-white rounded-3xl p-8 shadow-sm border ${feature.theme.border} ${feature.theme.hover} transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col`}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div
                  className={`h-12 w-12 rounded-2xl ${feature.theme.bg} flex items-center justify-center ${feature.theme.text} text-2xl`}
                >
                  <feature.icon />
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${feature.theme.pill}`}
                >
                  {feature.tag}
                </span>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover/card:text-blue-00 transition-colors">
                {feature.name}
              </h3>

              <p className="text-gray-600 mb-6 leading-relaxed">
                {feature.description}
              </p>

              {/* Visual Element */}
              <div className="mb-8">{feature.visual}</div>

              {/* Action */}
              <div className="mt-auto">
                <Button
                  label="Get Started"
                  onClick={() => navigate(feature.href)}
                  variant="secondary"
                  size="medium"
                  endIcon={<FaArrowRight />}
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Offers;
